/**
 * <SideNav>
 *
 * Desktop-only left sidebar. Lives inside the outer CSS grid track (16rem
 * wide) so it never overlaps content and never collapses to zero width.
 *
 * Interaction rules:
 *   • Each link has an animated left-indicator pill that slides between the
 *     active items using Framer Motion's shared layout (layoutId).
 *   • Hover / active states are explicit and visible on the warm-dark palette.
 *   • Every link is ≥ 44px tall for keyboard-and-touch parity.
 *   • The whole nav is inside `<aside>` for correct landmark semantics.
 */

import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@homejournal/utils';
import { NAV_ITEMS } from './nav-items';
import { springInteractive } from '@/components/common/motion-variants';
import { useScopes } from './AuthGate';

export function SideNav() {
  const { has } = useScopes();
  const items = NAV_ITEMS.filter((i) => !i.scope || has(i.scope));
  return (
    <aside
      aria-label="Primary"
      className={[
        // Desktop only — mobile uses <BottomNav>.
        'hidden md:flex',
        // Sit at the top of the grid track and never shrink.
        'shrink-0 flex-col',
        // Warm palette tuned for both light and dark modes.
        'border-earth-100 bg-earth-50/60 dark:bg-earth-950/60 border-r dark:border-white/[0.06]',
        // Comfortable internal padding.
        'px-4 py-6',
        // Own its own scroll if the item list ever exceeds the viewport.
        'overflow-y-auto',
      ].join(' ')}
    >
      {/* ── Brand ─────────────────────────────────────────────────────── */}
      <div className="mb-8 flex items-center gap-3 px-2">
        <div
          aria-hidden="true"
          className={[
            'grid h-11 w-11 place-items-center rounded-2xl',
            'bg-earth-800 text-earth-50 shadow-sm',
            'dark:bg-earth-100 dark:text-earth-900',
          ].join(' ')}
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5">
            <path
              d="M4 20V10l8-6 8 6v10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
            <path
              d="M10 20v-6h4v6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="truncate font-serif text-lg leading-tight tracking-tight">Home Journal</p>
          <p className="text-earth-500 dark:text-earth-400 truncate text-xs">
            a house becoming home
          </p>
        </div>
      </div>

      {/* ── Nav items ─────────────────────────────────────────────────── */}
      <nav className="flex flex-col gap-1" aria-label="Primary navigation">
        {items.map(({ to, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                // Layout + spacing.
                'group relative flex items-center gap-3 rounded-2xl px-3 py-2.5',
                // ≥ 44px hit target.
                'min-h-11',
                // Typography.
                'text-sm font-medium',
                // Interactive states.
                'transition-colors duration-200',
                'focus-visible:ring-brass-400 focus-visible:outline-none focus-visible:ring-2',
                isActive
                  ? 'text-earth-900 dark:text-earth-50'
                  : 'text-earth-600 hover:bg-earth-100/70 hover:text-earth-900 dark:text-earth-300 dark:hover:text-earth-50 dark:hover:bg-white/[0.04]',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/*
                  Animated active-state pill. Uses layoutId so Framer Motion
                  morphs the pill between rows instead of re-mounting it.
                  `pointer-events-none` guarantees it never blocks clicks.
                */}
                {isActive && (
                  <motion.span
                    layoutId="sidenav-active-pill"
                    aria-hidden="true"
                    className={[
                      'pointer-events-none absolute inset-0 rounded-2xl',
                      'bg-earth-100 dark:bg-white/[0.06]',
                    ].join(' ')}
                    transition={springInteractive}
                  />
                )}
                {/*
                  Icon + label sit above the pill via z-10. Not strictly
                  required (the pill has pointer-events-none) but keeps the
                  visual layering explicit.
                */}
                <Icon className="relative z-10 h-4 w-4" />
                <span className="relative z-10">{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Filler pushes anything below (future user card) to the bottom. */}
      <div className="mt-auto" />
    </aside>
  );
}
